
import { Requirement, RequirementStatus } from "@/types/project";
import { PieChart, CircleCheckIcon, XCircleIcon, RefreshCwIcon, AlertCircleIcon } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface ProjectStatsProps {
  requirements: Requirement[];
  requirementStatuses: RequirementStatus[];
}

export function ProjectStats({ requirements, requirementStatuses }: ProjectStatsProps) {
  // Calculate statistics
  const totalRequirements = requirements.length;
  const statusCounts = {
    'Ready to Submit': 0,
    'In Progress': 0,
    'Not Applicable': 0,
    'Incomplete': 0
  };

  // Count requirements by status
  requirements.forEach(req => {
    const status = requirementStatuses.find(s => 
      s.requirement_id === (req.requirement_id || req.id)
    )?.status || 'Incomplete';
    statusCounts[status]++;
  });

  // Calculate progress percentage (ready to submit / (total - not applicable))
  const relevantRequirements = totalRequirements - statusCounts['Not Applicable'];
  const progressPercentage = relevantRequirements > 0
    ? Math.round((statusCounts['Ready to Submit'] / relevantRequirements) * 100)
    : 0;

  return (
    <div className="mt-6 space-y-4">
      {/* Progress bar that spans all cards */}
      <div className="bg-muted/30 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Overall Progress</span>
          <span className="text-sm font-medium">{progressPercentage}%</span>
        </div>
        <Progress value={progressPercentage} className="h-2 w-full" />
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CircleCheckIcon className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium">Ready to Submit</span>
          </div>
          <p className="text-2xl font-bold">{statusCounts['Ready to Submit']}</p>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <RefreshCwIcon className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium">In Progress</span>
          </div>
          <p className="text-2xl font-bold">{statusCounts['In Progress']}</p>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <XCircleIcon className="h-4 w-4 text-yellow-500" />
            <span className="text-sm font-medium">Not Applicable</span>
          </div>
          <p className="text-2xl font-bold">{statusCounts['Not Applicable']}</p>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircleIcon className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium">Incomplete</span>
          </div>
          <p className="text-2xl font-bold">{statusCounts['Incomplete']}</p>
        </div>
      </div>
    </div>
  );
}
